import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { SupabankLogo } from '@/components/brand/SupabankLogo';
import { useBridgeVerify } from '@/hooks/useBridgeVerify';
import { isAdminDomain } from '@/lib/domains/admin';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { normalizeNextRoute as mapOnboardingRoute } from '@/lib/routing/onboardingRouteMap';
import { normalizeNextRoute as normalizeAuthRoute, getAuthSurface } from '@/lib/auth/normalizeNextRoute';
import { supabaseBrowser } from '@/lib/supabase-browser';
import { getOnboarding, isAuthRetryableError } from '@/lib/rpc/user/onboarding';
import { track } from '@/lib/telemetry';

type VerifyVariant = 'default' | 'start' | 'resume' | 'status';

type VerifyProps = {
  variant?: VerifyVariant;
};

const FALLBACK_COPY: Record<VerifyVariant, { title: string; description: string }> = {
  default: {
    title: 'Proceeding to Identity Verification',
    description: 'Just a moment... we are preparing your next step and will redirect you automatically.'
  },
  start: {
    title: 'Preparing Identity Verification',
    description: 'Synchronizing data and will open the Bridge form as soon as everything is ready.'
  },
  resume: {
    title: 'Returning to Identity Verification',
    description: 'Continuing your incomplete verification. This will only take a few seconds.'
  },
  status: {
    title: 'Loading Verification Status',
    description: 'Checking the current state of verification and will update the page automatically.'
  }
};

const STORAGE_NAMESPACE = 'verify';

const sessionStore = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch (error) {
    console.warn('[Verify] sessionStorage unavailable:', error);
    return null;
  }
};

const storageGet = (key: string) => {
  const storage = sessionStore();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    console.warn('[Verify] sessionStorage get failed:', error);
    return null;
  }
};

const storageSet = (key: string, value: string) => {
  const storage = sessionStore();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch (error) {
    console.warn('[Verify] sessionStorage set failed:', error);
  }
};

const safeRandomTrace = () => {
  const cryptoObj = globalThis?.crypto;
  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const buildStorageKey = (type: 'redirected' | 'tracked', trace: string) => `${STORAGE_NAMESPACE}:${type}:${trace}`;

export default function Verify({ variant = 'default' }: VerifyProps = {}) {
  const [verificationCode, setVerificationCode] = useState('');
  const [documentId, setDocumentId] = useState('');
  const verify = useBridgeVerify();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAttemptedRef = useRef(false);
  const isAdminSurface = isAdminDomain();
  const canonicalSelf = variant === 'default'
    ? '/verify'
    : variant === 'status'
      ? '/verify/status'
      : `/verify/${variant}`;

  const hasVerifyParams = useMemo(() => {
    const doc = searchParams.get('doc');
    const code = searchParams.get('code');
    return Boolean(doc && code);
  }, [searchParams]);

  useEffect(() => {
    // CANON auth-URL policy: /verify ONLY renders for doc+code params
    // Otherwise: call onboarding_user RPC and redirect to next_route
    if (isAdminSurface || hasVerifyParams || redirectAttemptedRef.current) {
      return;
    }

    const existingTrace = searchParams.get('v');
    if (existingTrace) {
      const redirectKey = buildStorageKey('redirected', existingTrace);
      if (storageGet(redirectKey)) {
        redirectAttemptedRef.current = true;
        return;
      }
    }

    let active = true;
    redirectAttemptedRef.current = true;

    const resolveRoute = async () => {
      try {
        const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

        const waitForSession = async (attempts = 20, baseDelay = 100) => {
          for (let attempt = 0; attempt < attempts; attempt++) {
            const { data } = await supabaseBrowser.auth.getSession();
            if (data.session?.user) {
              return data.session;
            }
            const backoff = baseDelay * Math.max(1, attempt + 1);
            await delay(backoff);
          }
          return null;
        };

        const ensureSession = async () => {
          let session = await waitForSession();
          if (session?.access_token) {
            return session;
          }

          const { error: refreshError } = await supabaseBrowser.auth.refreshSession();
          if (refreshError) {
            throw refreshError;
          }

          session = await waitForSession();
          return session;
        };

        const loadOnboarding = async (surface: ReturnType<typeof getAuthSurface>) => {
          const MAX_ATTEMPTS = 3;
          let delayMs = 150;

          for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
              return await getOnboarding(supabaseBrowser, {
                allowAdminContext: surface === 'admin'
              });
            } catch (error) {
              const message = error instanceof Error ? error.message : '';
              const shouldRetry = isAuthRetryableError(error);

              if (!shouldRetry || attempt === MAX_ATTEMPTS) {
                throw error;
              }

              await delay(delayMs);
              await ensureSession();
              delayMs *= 2;
            }
          }

          throw new Error('Failed to load onboarding state');
        };

        const session = await ensureSession();
        if (!session) {
          throw new Error('No active session');
        }

        // Call onboarding_user RPC to get next_route
        const detectedSurface = getAuthSurface();
        const onboarding = await loadOnboarding(detectedSurface);

        if (!active) return;

        // ✅ CRITICAL GUARD: If user is already approved, FORCE redirect to dashboard
        // This prevents redirect loop when onboarding_user RPC incorrectly returns 'start_kyc'
        // for already-verified users (data inconsistency between customers.kyc_status and kyc_providers)
        if (onboarding?.state === 'approved') {
          console.warn('[Verify] User already approved, forcing redirect to dashboard (ignoring next_route)');
          track('verify_approved_override', {
            original_next_route: onboarding.next_route,
            forced_destination: '/dashboard',
            trace_id: onboarding.trace_id
          });
          navigate('/dashboard', { replace: true });
          return;
        }

        // If user is stuck in 'pending_review' or 'in_progress' but still on /verify
        // AND we detected 'v' param (returning from Bridge), we should probably send them to dashboard too
        // unless they actually need to do something.
        if ((onboarding?.state === 'pending_review' || onboarding?.kyc_status === 'pending_review') && existingTrace) {
           console.warn('[Verify] User pending review after return, redirecting to dashboard');
           navigate('/dashboard', { replace: true });
           return;
        }

        // Normalize and redirect to next_route
        const mappedRoute = mapOnboardingRoute(onboarding?.next_route, {
          onboardingCompleted: onboarding?.onboarding_completed,
        });
        const rawTarget = normalizeAuthRoute(mappedRoute, detectedSurface);
        const currentWithSearch = `${location.pathname}${location.search}`;

        const redirectTrace = existingTrace || safeRandomTrace();
        const trackKey = buildStorageKey('tracked', redirectTrace);
        const redirectKey = buildStorageKey('redirected', redirectTrace);

        let destination = rawTarget;

        if (destination?.startsWith('/kyc')) {
          const url = new URL(destination, window.location.origin);
          url.searchParams.set('v', redirectTrace);
          destination = `${url.pathname}${url.search}`;
          storageSet(redirectKey, '1');
        }

        if (destination && storageGet(trackKey) !== '1') {
          storageSet(trackKey, '1');
          console.info('[Verify] No params → redirect:', destination);
          track('verify_autoredirect', {
            surface: detectedSurface,
            next_route: destination,
            trace_id: onboarding.trace_id,
            raw_next_route: rawTarget,
            redirect_trace: redirectTrace,
            reused_trace: Boolean(existingTrace)
          });
        }

        if (destination && destination !== currentWithSearch) {
          navigate(destination, { replace: true });
          return;
        }

        if ((!destination || destination === currentWithSearch) && canonicalSelf !== location.pathname) {
          navigate(canonicalSelf, { replace: true });
        }
      } catch (error) {
        if (!active) return;
        console.error('[Verify] Route resolution error:', error);
        if (error instanceof Error && error.message === 'No active session') {
          navigate('/auth/login', { replace: true });
          return;
        }

        const fallbackOnError = isAdminSurface ? '/admin' : '/dashboard';
        navigate(fallbackOnError, { replace: true });
      }
    };

    void resolveRoute();

    return () => {
      active = false;
    };
  }, [hasVerifyParams, navigate, location.pathname, location.search, canonicalSelf, isAdminSurface]);

  const handleVerify = async () => {
    if (!verificationCode.trim() || !documentId.trim()) return;

    await verify.submit({
      code: verificationCode.trim(),
      documentId: documentId.trim()
    });
  };

  const verificationContent = (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-center mb-6">
          <SupabankLogo variant="full" size="md" />
        </div>
        <h1 className="font-display text-heading-md mb-6">Bridge Document Verification</h1>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Document ID</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Enter document ID..."
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Verification Code</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Enter verification code..."
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleVerify}
              disabled={verify.loading || !verificationCode.trim() || !documentId.trim()}
              className="px-4 py-2 bg-black text-white rounded text-sm disabled:bg-gray-300"
            >
              {verify.loading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              onClick={() => {
                setVerificationCode('');
                setDocumentId('');
                verify.reset();
              }}
              className="px-4 py-2 border rounded text-sm"
            >
              Clear
            </button>
          </div>

          {verify.error && (
            <div className="mt-4 p-3 bg-destructive/5 border border-destructive/20 rounded text-sm text-destructive">
              {verify.error}
            </div>
          )}

          {verify.result && (
            <div className="mt-4 p-3 bg-success/5 border border-success/20 rounded text-sm">
              <div className="font-medium text-success mb-2">Verification Successful</div>
              <div className="text-success">
                <div>Status: {verify.result.status}</div>
                <div>Document: {verify.result.documentId}</div>
                {verify.result.timestamp && (
                  <div>Time: {new Date(verify.result.timestamp).toLocaleString()}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {verify.history && verify.history.length > 0 && (
        <div className="mt-6 bg-white border rounded-lg p-6">
          <h2 className="font-medium mb-4">Verification History</h2>
          <div className="space-y-2">
            {verify.history.map((item, index) => (
              <div key={index} className="flex justify-between items-center p-2 border rounded text-sm">
                <div>
                  <span className="font-medium">{item.documentId}</span>
                  <span className="ml-2 text-gray-500">({item.status})</span>
                </div>
                <div className="text-gray-500">
                  {new Date(item.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const fallbackCopy = FALLBACK_COPY[variant] ?? FALLBACK_COPY.default;

  const fallbackContent = (
    <div className="min-h-screen bg-slate-100 py-10">
      <div className="container mx-auto px-4">
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 rounded-xl bg-white p-8 text-center shadow-sm">
          <SupabankLogo variant="full" size="md" className="mb-2" />
          <InlineLoadingIndicator
            size="xl"
            aria-label="Loading verification status"
          />
          <div className="space-y-2">
            <h1 className="font-display text-heading-md">{fallbackCopy.title}</h1>
            <p className="text-sm text-muted-foreground">{fallbackCopy.description}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isAdminSurface) {
    return (
      <div className="min-h-screen bg-slate-100 py-10">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-lg rounded-xl bg-white p-8 text-center shadow-sm">
            <div className="flex justify-center mb-4">
              <SupabankLogo variant="icon" size="lg" className="opacity-50" />
            </div>
            <h1 className="font-display text-heading-md">Verification Unavailable</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              This section is only available in the user portal. Please use the public domain supabank.xyz
              to complete identity verification.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasVerifyParams) {
    return fallbackContent;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-10">
      <div className="container mx-auto px-4">
        {verificationContent}
      </div>
    </div>
  );
}
